import { AxiosInstance } from 'axios'
import { useMutation, useQueryClient } from 'react-query'

import { TeamDetailsData } from '@dc-shared-web/hooks/api/team'
import { User, UserSearchResult } from '@dc-shared-web/hooks/api/user'

import { useAxiosContext } from '@dc-shared-web/providers/AxiosProvider'

import { useToast } from '@dc-shared-web/components/shadcn/use-toast'

type UpdateUserParams = {
  userUUID: string
  enabled?: boolean
  teamUUIDs?: string[]
  role?: string
}

const updateUser = async (
  apiClient: AxiosInstance,
  params: UpdateUserParams
): Promise<TeamDetailsData> => {
  const { data } = await apiClient.patch(`/api/users/${params.userUUID}`, {
    enabled: params.enabled,
    teamUUIDs: params.teamUUIDs,
    role: params.role,
  })

  return data.data
}

export const useUpdateUser = (successMessage: string) => {
  const apiClient = useAxiosContext()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation(
    (params: UpdateUserParams) => updateUser(apiClient, params),
    {
      onSuccess: async (_, params: UpdateUserParams) => {
        await queryClient.invalidateQueries<User>(`user-${params.userUUID}`, {
          refetchActive: true,
          refetchInactive: true,
        })
        await queryClient.invalidateQueries<UserSearchResult>({
          queryKey: ['searchUser'],
          refetchPage: (page) => {
            return Boolean(page.data.find((e) => e.uuid === params.userUUID))
          },
        })

        toast({
          variant: 'success',
          title: successMessage,
        })
      },
      onError: () => {
        toast({
          variant: 'error',
          title: 'Error updating user',
          description: 'If the issue persists, please contact support.',
        })
      },
    }
  )
}
